import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsCompact } from '../Layout/shellState';

/**
 * Below 1024px an open conversation (or request) is a screen of its own, so a phone's Back button
 * has to close it instead of leaving the page. While a thread is open on such a screen it holds ONE
 * history entry: the same URL, marked in the router state under `key`. Back pops that entry and the
 * page is told with `onClosed()`; the thread's own back button goes through the same door
 * (`back()`), so the two always agree. From 1024px up the thread is a pane beside the list and
 * nothing is pushed: `back()` just calls `onClosed()`.
 *
 *   const history = useThreadHistory('chatRoom', closeRoom);
 *   const open = (room) => { setRoom(room); history.opened(); };
 *   <button onClick={history.back}>…</button>
 *
 * `onClosed` must be safe to call with nothing open (an entry left behind by an earlier visit).
 */
export default function useThreadHistory(key, onClosed) {
  const location = useLocation();
  const navigate = useNavigate();
  const compact = useIsCompact();
  const marked = Boolean(location.state && location.state[key]);

  const closedRef = useRef(onClosed);
  closedRef.current = onClosed;
  const seenRef = useRef(false); // the marked entry has been the current one

  // The marked entry went away (Back, or `back()` below): the thread is closed.
  useEffect(() => {
    if (marked) {
      seenRef.current = true;
    } else if (seenRef.current) {
      seenRef.current = false;
      closedRef.current();
    }
  }, [marked]);

  // A page that mounts on a marked entry (Back from another page, or a reload) has no thread open:
  // the mark is stale, and a Back press that closes nothing would feel dead. Clear it once.
  const state = location.state;
  const pathname = location.pathname;
  const search = location.search;
  const hash = location.hash;
  useEffect(() => {
    if (!(state && state[key])) return;
    const { [key]: _stale, ...rest } = state;
    seenRef.current = false;
    navigate({ pathname, search, hash }, { replace: true, state: Object.keys(rest).length ? rest : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on mount
  }, []);

  const opened = useCallback(() => {
    if (!compact || marked) return;
    navigate({ pathname, search, hash }, { state: { ...(state || {}), [key]: true } });
  }, [compact, marked, navigate, pathname, search, hash, state, key]);

  const back = useCallback(() => {
    if (compact && marked) navigate(-1); // the effect above closes the thread once the entry is gone
    else closedRef.current();
  }, [compact, marked, navigate]);

  return { opened, back };
}
